import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Receipt } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useMyProperties } from "@/lib/hooks";
import { PageHeader, Money, Empty, StatusBadge } from "@/components/common";
import { PropertySelect, CurrencySelect } from "@/components/FormBits";
import { meterLabel } from "@/components/Meters";
import { Field } from "@/components/PropertyDialog";
import { DateInput } from "@/components/DateInput";

const today = () => new Date().toISOString().slice(0, 10);
const Btn = ({ children, ...p }) => <Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" {...p}>{children}</Button>;

function ReadingDialog() {
  const { t, lang } = usePrefs();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ property_id: "", meter: "", value: "", date: today() });
  const { data: prop } = useQuery({ queryKey: ["property", f.property_id], queryFn: () => fetcher(`/properties/${f.property_id}`), enabled: !!f.property_id });
  const meters = prop ? [...(prop.meters || []).map((m) => ({ ...m, unit_id: null, ref: prop.name })), ...prop.units.flatMap((u) => (u.meters || []).map((m) => ({ ...m, unit_id: u.id, ref: u.reference })))] : [];
  const save = async () => {
    const m = meters.find((x) => x.number === f.meter);
    try {
      await api.post("/readings", { property_id: f.property_id, unit_id: m.unit_id, meter_number: m.number, type: m.type, value: Number(f.value), date: f.date });
      qc.invalidateQueries({ queryKey: ["readings"] });
      qc.invalidateQueries({ queryKey: ["property", f.property_id] });
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Btn data-testid="new-reading-button"><Plus size={16} className="mr-1" />{t("newReading")}</Btn></DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="reading-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newReading")}</DialogTitle></DialogHeader>
        <Field label={t("property")}><PropertySelect value={f.property_id} onChange={(v) => setF({ ...f, property_id: v, meter: "" })} testId="reading-property-select" /></Field>
        <Field label={t("meter")}>
          <Select value={f.meter} onValueChange={(v) => setF({ ...f, meter: v })}>
            <SelectTrigger data-testid="reading-meter-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{meters.map((m) => <SelectItem key={m.number} value={m.number}>{m.ref} · {meterLabel(m.type, lang)} · {m.number} (index {m.last_reading ?? "—"})</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("value")}><Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="reading-value-input" /></Field>
          <Field label={t("date")}><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="reading-date-input" /></Field>
        </div>
        <Btn onClick={save} disabled={!f.meter || f.value === ""} data-testid="reading-save-button">{t("save")}</Btn>
      </DialogContent>
    </Dialog>
  );
}

function ChargeDialog() {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ property_id: "", label: "", period: "", amount: "", currency: "EUR", key: "surface" });
  const save = async () => {
    try { await api.post("/charges", { ...f, amount: Number(f.amount) }); qc.invalidateQueries({ queryKey: ["charges"] }); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Btn data-testid="new-charge-button"><Plus size={16} className="mr-1" />{t("newCharge")}</Btn></DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="charge-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newCharge")}</DialogTitle></DialogHeader>
        <Field label={t("property")}><PropertySelect value={f.property_id} onChange={(v) => setF({ ...f, property_id: v })} testId="charge-property-select" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("label")}><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Eau parties communes" data-testid="charge-label-input" /></Field>
          <Field label={t("period")}><Input value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} placeholder="T3 2026" data-testid="charge-period-input" /></Field>
          <Field label={t("amount")}><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="charge-amount-input" /></Field>
          <Field label={t("currency")}><CurrencySelect value={f.currency} onChange={(v) => setF({ ...f, currency: v })} testId="charge-currency-select" /></Field>
        </div>
        <Field label={t("allocKey")}>
          <Select value={f.key} onValueChange={(v) => setF({ ...f, key: v })}>
            <SelectTrigger data-testid="charge-key-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="surface">{t("bySurface")}</SelectItem><SelectItem value="equal">{t("equal")}</SelectItem></SelectContent>
          </Select>
        </Field>
        <Btn onClick={save} disabled={!f.property_id || !f.label || !f.amount} data-testid="charge-save-button">{t("create")}</Btn>
      </DialogContent>
    </Dialog>
  );
}

function ChargeCard({ c, staff, propName }) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const run = async (fn) => { try { await fn(); qc.invalidateQueries({ queryKey: ["charges"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`charge-card-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><div className="eyebrow">{propName} · {c.period}</div><h3 className="mt-1 font-display text-xl font-bold">{c.label}</h3><div className="text-xs text-slate-500">{t(c.key === "surface" ? "bySurface" : "equal")}</div></div>
        <div className="flex items-center gap-3">
          <Money amount={c.amount} currency={c.currency} />
          {c.invoiced ? <StatusBadge value="paid" label={t("invoiced")} /> : staff && <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs" onClick={() => run(() => api.post(`/charges/${c.id}/invoice`))} data-testid={`charge-invoice-${c.id}`}><Receipt size={13} className="mr-1" />{t("invoiceTenants")}</Button>}
          {staff && <button onClick={() => run(() => api.delete(`/charges/${c.id}`))} className="text-slate-500 hover:text-rose-600" data-testid={`charge-delete-${c.id}`}><Trash2 size={14} /></button>}
        </div>
      </div>
      <div className="mt-4 grid gap-1.5 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {c.allocations.map((a) => (
          <div key={a.unit_id} className="flex justify-between rounded-sm bg-slate-50 px-3 py-1.5"><span className="font-data text-xs">{a.unit_ref} <span className="text-slate-400">{a.share}%</span></span><Money amount={a.amount} currency={c.currency} showContract={false} /></div>
        ))}
      </div>
    </div>
  );
}

export default function Charges() {
  const { user } = useAuth();
  const { t, fdate, lang } = usePrefs();
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const props = useMyProperties();
  const names = Object.fromEntries(props.map((p) => [p.id, p.name]));
  const { data: readings = [] } = useQuery({ queryKey: ["readings"], queryFn: () => fetcher("/readings") });
  const { data: charges = [] } = useQuery({ queryKey: ["charges"], queryFn: () => fetcher("/charges") });
  return (
    <div data-testid="charges-page">
      <PageHeader eyebrow={t("secAsset")} title={t("meterCharges")}>{staff && <><ReadingDialog /><ChargeDialog /></>}</PageHeader>
      <Tabs defaultValue="readings">
        <TabsList className="mb-5 rounded-sm">
          <TabsTrigger value="readings" data-testid="charges-tab-readings">{t("readings")}</TabsTrigger>
          <TabsTrigger value="charges" data-testid="charges-tab-charges">{t("commonCharges")}</TabsTrigger>
        </TabsList>
        <TabsContent value="readings">
          {readings.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto rounded-sm border bg-white">
              <table className="w-full min-w-[640px] text-sm">
                <thead><tr className="border-b text-left text-[10px] uppercase tracking-[0.14em] text-slate-500"><th className="py-2 pl-4">{t("date")}</th><th>{t("property")}</th><th>{t("meter")}</th><th>{t("value")}</th><th>{t("consumption")}</th><th>Par</th></tr></thead>
                <tbody>{readings.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`reading-row-${r.id}`}>
                    <td className="py-2.5 pl-4">{fdate(r.date)}</td><td>{names[r.property_id]}{r.unit_ref ? ` · ${r.unit_ref}` : ""}</td>
                    <td>{meterLabel(r.type, lang)} <span className="font-data text-xs text-slate-400">{r.meter_number}</span></td>
                    <td className="font-data">{r.value}</td><td className="font-data">{r.consumption ?? "—"}</td><td className="text-xs text-slate-500">{r.by}</td>
                  </tr>))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="charges"><div className="space-y-4">{charges.length === 0 && <Empty />}{charges.map((c) => <ChargeCard key={c.id} c={c} staff={staff} propName={names[c.property_id]} />)}</div></TabsContent>
      </Tabs>
    </div>
  );
}
